import { useQuery } from '@tanstack/react-query';
import { api, unwrap } from '../lib/api.ts';

/**
 * Inferred from the route rather than imported from `@starter/db`: the browser
 * has no business resolving the database package to learn the shape of a JSON
 * response, and inference means a change to the catalog row breaks this file
 * in `pnpm typecheck` the same way it would break a hand-written type — minus
 * the hand-written type.
 */
type CatalogResponse = Awaited<
  ReturnType<Awaited<ReturnType<(typeof api.api.catalogs)[':name']['$get']>>['json']>
>;
export type CatalogItem = CatalogResponse['catalog']['items'][number];

/**
 * A dictionary, read from the API rather than restated in the bundle.
 *
 * The temptation this replaces is a `const ROLES = [...]` next to the component
 * that renders them, which works until a row is added and the deploy that
 * should not have been needed is needed. `apps/api/src/catalogs/
 * no-duplicate-enums.test.ts` is what makes that temptation fail in CI.
 *
 * Cached for an hour and never refetched on focus: a catalog changes when a
 * migration changes it, which is a few times a year, and a dropdown that
 * re-fetches its options every time the window regains focus is a request per
 * tab switch for nothing.
 */
const HOUR = 60 * 60 * 1000;

export const catalogKey = (name: string) => ['catalog', name] as const;

export interface UseCatalog {
  items: CatalogItem[];
  isPending: boolean;
  error: Error | null;
  /** The label in the current language, falling back to the key. */
  label: (key: string) => string;
  /** The row's hint in the current language, empty when it has none. */
  hint: (key: string) => string;
}

export function useCatalog(name: string, locale = 'en'): UseCatalog {
  const query = useQuery({
    queryKey: catalogKey(name),
    queryFn: () => unwrap(api.api.catalogs[':name'].$get({ param: { name } })),
    staleTime: HOUR,
    gcTime: HOUR,
    refetchOnWindowFocus: false,
  });

  const items = query.data?.catalog.items ?? [];

  return {
    items,
    isPending: query.isPending,
    error: query.error,
    // The key is a deliberate fallback rather than an empty string: a missing
    // translation should read as a slightly ugly screen, not as a blank one.
    label: (key: string) => {
      const found = items.find((item) => item.key === key);
      return found?.label[locale] ?? found?.label.en ?? key;
    },
    /**
     * The row's second sentence, where it has one.
     *
     * Empty rather than the key when it is missing, which is the opposite of
     * `label` and right for the same reason: a label is the thing's name and
     * has to say something, a hint is an explanation and a screen without one
     * is a screen with less text, not a broken screen.
     *
     * Here rather than at each call site because reading a localised field out
     * of a catalog row is exactly the kind of three-line thing that gets
     * written out per screen and then diverges (`docs/CATALOGS.md`).
     */
    hint: (key: string) => {
      const found = items.find((item) => item.key === key);
      const hint = found?.hint as Record<string, string> | undefined;
      return hint?.[locale] ?? hint?.en ?? '';
    },
  };
}

/** The index of every catalog this deployment serves. */
export function useCatalogs() {
  return useQuery({
    queryKey: ['catalogs'],
    queryFn: () => unwrap(api.api.catalogs.$get()),
    staleTime: HOUR,
    refetchOnWindowFocus: false,
  });
}
